import React, { useState, useEffect, useCallback } from 'react';
import './HeroCardPreviewShell.css';
import './EtelapHeroCardPreview.css';
import etlapCardImg from '../img/etlapcard.png';
import {
  fetchCurrentWeekMenus,
  pickPreviewMenu,
  buildPreviewRows,
} from '../utils/menuHelpers';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const MEAL_SVG = {
  soup: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 15c0 3 2.5 5 7 5s7-2 7-5M7 11h10M9 7c0-1.5 1-2.5 3-2.5s3 1 3 2.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  meat: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M7 17c-2.5 0-4-1.5-4-4 0-2.5 2-5 5-5 1.5 0 3 .5 4 2M17 9c1.5 0 3 1.5 3 3.5S18.5 16 17 16"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  ),
  pasta: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <ellipse cx="12" cy="14" rx="7" ry="3" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 10c0-2 1.5-3.5 4-3.5s4 1.5 4 3.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  ),
};

function mealSvgForIcon(emojiIcon) {
  if (emojiIcon === '🍲') return MEAL_SVG.soup;
  if (emojiIcon === '🍗') return MEAL_SVG.meat;
  if (emojiIcon === '🍝') return MEAL_SVG.pasta;
  return MEAL_SVG.soup;
}

const EtelapHeroCardPreview = ({ onNavigate }) => {
  const [rows, setRows] = useState(() => buildPreviewRows(null, 3));
  const [loading, setLoading] = useState(true);

  const loadMenus = useCallback(async () => {
    setLoading(true);
    try {
      const menus = await fetchCurrentWeekMenus(API_BASE);
      const menu = pickPreviewMenu(menus);
      setRows(buildPreviewRows(menu, 3));
    } catch {
      setRows(buildPreviewRows(null, 3));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMenus();
  }, [loadMenus]);

  const handleCta = (e) => {
    e.stopPropagation();
    if (onNavigate) onNavigate();
  };

  return (
    <div
      className="hero-card-shell etlap-hero-preview"
      role="presentation"
      onClick={onNavigate}
    >
      <div className="hero-card-shell__header">
        <span className="hero-card-shell__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M8 3v8M8 14v7M12 3v18M16 3v8M16 14v7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </span>
        <div>
          <h3 className="hero-card-shell__title etlap-hero-preview__title">Heti Étlap</h3>
          <p className="hero-card-shell__subtitle">
            Fedezze fel változatos és kiegyensúlyozott heti étlapunkat. Friss
            alapanyagokból készülő fogások, több diétás kategóriában, minden nap.
          </p>
        </div>
      </div>

      <div className="hero-card-shell__panel etlap-hero-preview__panel">
        <div className="etlap-hero-preview__body">
          <div className="etlap-hero-preview__food-wrap">
            <img
              src={etlapCardImg}
              alt=""
              className="etlap-hero-preview__food"
              loading="lazy"
              decoding="async"
            />
          </div>

          <div className="etlap-hero-preview__menu">
            <div className="etlap-hero-preview__panel-head">
              <span className="etlap-hero-preview__panel-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.6" />
                  <path d="M4 9h16M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </span>
              <span>Heti kínálat</span>
            </div>

            {loading ? (
              <p className="etlap-hero-preview__muted">Betöltés…</p>
            ) : (
              <ul className="etlap-hero-preview__list">
                {rows.map((row) => (
                  <li
                    key={`${row.dayLabel}-${row.dayNum}-${row.title}`}
                    className="etlap-hero-preview__row"
                  >
                    <div className="etlap-hero-preview__date">
                      <span className="etlap-hero-preview__dow">
                        {row.dayLabel === 'CSÜTÖRTÖK' ? 'CSÜT.' : row.dayLabel}
                      </span>
                      <span className="etlap-hero-preview__day">{row.dayNum}</span>
                      <span className="etlap-hero-preview__mon">{row.monthAbbrev}</span>
                    </div>
                    <div className="etlap-hero-preview__meal">
                      <span className="etlap-hero-preview__meal-title">{row.title}</span>
                      {row.sides ? (
                        <span className="etlap-hero-preview__meal-sides">{row.sides}</span>
                      ) : null}
                    </div>
                    <span className="etlap-hero-preview__meal-icon" aria-hidden="true">
                      {mealSvgForIcon(row.icon)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <p className="etlap-hero-preview__badge">
              <span className="etlap-hero-preview__badge-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 21c-5-3-7-7-7-11a7 7 0 0 1 14 0c0 4-2 8-7 11z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path d="M12 21V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </span>
              Friss alapanyagok, otthonos ízek
            </p>
          </div>
        </div>
      </div>

      <button type="button" className="hero-card-shell__cta" onClick={handleCta}>
        ÉTLAP MEGTEKINTÉSE <span aria-hidden="true">→</span>
      </button>
    </div>
  );
};

export default EtelapHeroCardPreview;
